import axios, { AxiosHeaders } from 'axios'
import { useAlertStore } from '@shared/store'
import {
  clearAuthSessionAndRedirectToLogin,
  isUnauthorizedSession,
  trimRequestValues,
} from '@shared/utils'
import { getClientPlatformHeader, getDeviceTypeHeader } from './deviceHeaders'

type SilentRequestConfig = {
  suppressGlobalErrorHandler?: boolean
}

const shouldSuppressGlobalErrorHandler = (config: unknown) =>
  !!(config as SilentRequestConfig | undefined)?.suppressGlobalErrorHandler

export const apiInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

apiInstance.interceptors.request.use(config => {
  config.data = trimRequestValues(config.data)
  config.params = trimRequestValues(config.params)

  const headers = AxiosHeaders.from(config.headers)
  headers.set('X-Platform', getClientPlatformHeader())

  const deviceType = getDeviceTypeHeader()
  if (deviceType) {
    headers.set('device-Type', deviceType)
  } else {
    headers.delete('device-Type')
  }

  config.headers = headers

  return config
})

apiInstance.interceptors.response.use(
  response => {
    if (shouldSuppressGlobalErrorHandler(response.config)) return response

    const body = response?.data
    if (body && typeof body === 'object' && 'code' in body) {
      const code = (body as { code?: number }).code
      const message = (body as { message?: string }).message

      if (code !== undefined && code !== 200) {
        if (isUnauthorizedSession(code, message)) {
          clearAuthSessionAndRedirectToLogin()
          return response
        }

        useAlertStore.getState().push(message || '오류가 발생했습니다.')
      }
    }
    return response
  },
  error => {
    if (shouldSuppressGlobalErrorHandler(error.config)) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      clearAuthSessionAndRedirectToLogin()
      return Promise.reject(error)
    }

    const body = error.response?.data
    const message =
      (body && typeof body === 'object' && 'message' in body
        ? (body as { message?: string }).message
        : undefined) ??
      error.message ??
      '오류가 발생했습니다.'
    useAlertStore.getState().push(message)
    return Promise.reject(error)
  },
)
